
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

export default function OurProcessPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12 bg-gradient-to-br from-[#F0F7ED] to-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#093a3e]/10 px-4 py-2 rounded-full mb-6">
            <span className="w-2 h-2 bg-[#093a3e] rounded-full animate-pulse"></span>
            <span className="text-xs font-semibold text-[#093a3e] uppercase tracking-wide">Our Process</span>
          </div>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            A turnkey screening program that integrates into real workflows.
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed">
            Kinra combines on-site and mobile operations with software to drive measurable improvement in diabetic eye exam screening performance.
          </p>
        </div>
      </section>

      {/* Step-by-Step Timeline */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Step-by-step deployment
            </h2>
            <p className="text-lg text-gray-600">
              From baseline analysis to continuous optimization
            </p>
          </div>

          {/* Timeline */}
          <div className="relative">
            {/* Vertical Line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-[#093a3e]/30"></div>

            {/* Step 1 */}
            <div className="relative mb-16">
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
                <div className="lg:text-right mb-8 lg:mb-0">
                  <div className="inline-block lg:block">
                    <div className="inline-flex items-center gap-3 bg-[#093a3e]/10 px-4 py-2 rounded-full mb-4">
                      <span className="w-10 h-10 bg-[#093a3e] text-white rounded-full flex items-center justify-center font-bold">1</span>
                      <span className="font-semibold text-[#093a3e]">Population Targeting</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Population targeting & baseline analysis
                    </h3>
                    <ul className="text-gray-600 space-y-2 text-left lg:text-right">
                      <li>
                        <span>Confirm measurement definition and eligible population</span>
                      </li>
                      <li>
                        <span>Establish baseline screening rates and gap counts</span>
                      </li>
                      <li>
                        <span>Segment by geography, site, risk, and engagement likelihood</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="lg:pl-12">
                  <div className="bg-[#FAFAF9] rounded-2xl p-8 shadow-sm">
                    <div className="w-16 h-16 bg-[#093a3e]/20 rounded-2xl flex items-center justify-center mb-4">
                      <i className="ri-pie-chart-line text-[#093a3e] text-3xl"></i>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      We start by understanding your population, establishing clear baselines, and identifying the highest-impact opportunities for screening improvement.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative mb-16">
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
                <div className="lg:order-2 mb-8 lg:mb-0">
                  <div className="inline-block lg:block">
                    <div className="inline-flex items-center gap-3 bg-[#093a3e]/10 px-4 py-2 rounded-full mb-4">
                      <span className="w-10 h-10 bg-[#093a3e] text-white rounded-full flex items-center justify-center font-bold">2</span>
                      <span className="font-semibold text-[#093a3e]">Workflow Design</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Workflow design & site activation
                    </h3>
                    <ul className="text-gray-600 space-y-2">
                      <li>
                        <span>Identify point-of-care settings (primary care, endocrinology, community sites)</span>
                      </li>
                      <li>
                        <span>Define staffing model and patient flow (5–10 minutes added, designed to be minimal)</span>
                      </li>
                      <li>
                        <span>Set routing rules for results and follow-up pathways</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="lg:order-1 lg:pr-12">
                  <div className="bg-[#FAFAF9] rounded-2xl p-8 shadow-sm">
                    <div className="w-16 h-16 bg-[#093a3e]/20 rounded-2xl flex items-center justify-center mb-4">
                      <i className="ri-settings-3-line text-[#093a3e] text-3xl"></i>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      We design workflows that fit seamlessly into existing care delivery, minimizing disruption while maximizing screening completion.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative mb-16">
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
                <div className="lg:text-right mb-8 lg:mb-0">
                  <div className="inline-block lg:block">
                    <div className="inline-flex items-center gap-3 bg-[#093a3e]/10 px-4 py-2 rounded-full mb-4">
                      <span className="w-10 h-10 bg-[#093a3e] text-white rounded-full flex items-center justify-center font-bold">3</span>
                      <span className="font-semibold text-[#093a3e]">Screening Execution</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Screening execution (embedded + mobile)
                    </h3>
                    <ul className="text-gray-600 space-y-2 text-left lg:text-right">
                      <li>
                        <span>Embedded staff operate in-clinic for high-throughput screening</span>
                      </li>
                      <li>
                        <span>Mobile dispatch targets members who are least likely to complete referrals</span>
                      </li>
                      <li>
                        <span>Outreach built around convenience, trust, and timing</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="lg:pl-12">
                  <div className="bg-[#FAFAF9] rounded-2xl p-8 shadow-sm">
                    <div className="w-16 h-16 bg-[#093a3e]/20 rounded-2xl flex items-center justify-center mb-4">
                      <i className="ri-hospital-line text-[#093a3e] text-3xl"></i>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Our dual approach combines in-clinic screening for high-volume settings with mobile teams that reach patients where traditional care delivery falls short.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="relative mb-16">
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
                <div className="lg:order-2 mb-8 lg:mb-0">
                  <div className="inline-block lg:block">
                    <div className="inline-flex items-center gap-3 bg-[#093a3e]/10 px-4 py-2 rounded-full mb-4">
                      <span className="w-10 h-10 bg-[#093a3e] text-white rounded-full flex items-center justify-center font-bold">4</span>
                      <span className="font-semibold text-[#093a3e]">Results & Follow-up</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Results, documentation & follow-up
                    </h3>
                    <ul className="text-gray-600 space-y-2">
                      <li>
                        <span>Automated results generation and documentation support</span>
                      </li>
                      <li>
                        <span>Clear escalation for positive findings</span>
                      </li>
                      <li>
                        <span>Referral coordination options (plan/system-dependent)</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="lg:order-1 lg:pr-12">
                  <div className="bg-[#FAFAF9] rounded-2xl p-8 shadow-sm">
                    <div className="w-16 h-16 bg-[#093a3e]/20 rounded-2xl flex items-center justify-center mb-4">
                      <i className="ri-file-text-line text-[#093a3e] text-3xl"></i>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      We ensure every screening translates into proper documentation and appropriate follow-up, closing the loop from detection to care coordination.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="relative">
              <div className="lg:grid lg:grid-cols-2 lg:gap-12 items-center">
                <div className="lg:text-right mb-8 lg:mb-0">
                  <div className="inline-block lg:block">
                    <div className="inline-flex items-center gap-3 bg-[#093a3e]/10 px-4 py-2 rounded-full mb-4">
                      <span className="w-10 h-10 bg-[#093a3e] text-white rounded-full flex items-center justify-center font-bold">5</span>
                      <span className="font-semibold text-[#093a3e]">Optimization</span>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">
                      Performance tracking & continuous optimization
                    </h3>
                    <ul className="text-gray-600 space-y-2 text-left lg:text-right">
                      <li>
                        <span>Monthly reporting on screening completion, positivity rates, and site performance</span>
                      </li>
                      <li>
                        <span>Targeted outreach iterations to maximize closure</span>
                      </li>
                      <li>
                        <span>End-of-year measurement readiness</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="lg:pl-12">
                  <div className="bg-[#FAFAF9] rounded-2xl p-8 shadow-sm">
                    <div className="w-16 h-16 bg-[#093a3e]/20 rounded-2xl flex items-center justify-center mb-4">
                      <i className="ri-dashboard-line text-[#093a3e] text-3xl"></i>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      Continuous monitoring and optimization ensure you're on track to meet quality targets and maximize the impact of every screening completed.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Provide Section */}
      <section className="py-24 px-6 lg:px-12 bg-[#FAFAF9]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What we provide
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need for a successful screening program
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-[#093a3e]/20 rounded-full flex items-center justify-center mb-4">
                <i className="ri-camera-line text-[#093a3e] text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Handheld imaging devices and deployment logistics
              </h4>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-[#093a3e]/20 rounded-full flex items-center justify-center mb-4">
                <i className="ri-team-line text-[#093a3e] text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Trained screening professionals
              </h4>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-[#093a3e]/20 rounded-full flex items-center justify-center mb-4">
                <i className="ri-computer-line text-[#093a3e] text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Software for workflow + reporting support
              </h4>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-[#093a3e]/20 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-user-line text-[#093a3e] text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Member outreach playbooks (site-based and mobile)
              </h4>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all">
              <div className="w-14 h-14 bg-[#093a3e]/20 rounded-full flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-[#093a3e] text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Compliance-forward documentation practices
              </h4>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all border-2 border-[#093a3e]">
              <div className="w-14 h-14 bg-[#093a3e] rounded-full flex items-center justify-center mb-4">
                <i className="ri-money-dollar-circle-line text-white text-2xl"></i>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">
                Optional performance-based pricing
              </h4>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline Overview Section */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Typical deployment timeline
            </h2>
            <p className="text-lg text-gray-600">
              From kickoff to full-scale operation
            </p>
          </div>

          <div className="relative">
            {/* Horizontal Timeline Bar */}
            <div className="hidden md:block absolute top-8 left-0 right-0 h-1 bg-[#093a3e]/30"></div>
            
            <div className="grid md:grid-cols-4 gap-8 relative">
              {/* Week 1-2 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-[#093a3e] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold relative z-10">
                  1-2
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Weeks 1-2</h4>
                <p className="text-sm text-gray-600">
                  Population analysis & workflow design
                </p>
              </div>

              {/* Week 3-4 */}
              <div className="text-center">
                <div className="w-16 h-16 bg-[#093a3e] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold relative z-10">
                  3-4
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Weeks 3-4</h4>
                <p className="text-sm text-gray-600">
                  Site activation & staff training
                </p>
              </div>

              {/* Week 5+ */}
              <div className="text-center">
                <div className="w-16 h-16 bg-[#093a3e] text-white rounded-full flex items-center justify-center mx-auto mb-4 text-lg font-bold relative z-10">
                  5+
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Week 5+</h4>
                <p className="text-sm text-gray-600">
                  Full screening operations launch
                </p>
              </div>

              {/* Ongoing */}
              <div className="text-center">
                <div className="w-16 h-16 bg-[#0d4a50] text-white rounded-full flex items-center justify-center mx-auto mb-4 relative z-10">
                  <i className="ri-loop-right-line text-2xl"></i>
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Ongoing</h4>
                <p className="text-sm text-gray-600">
                  Monthly optimization & reporting
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 lg:px-12 bg-gray-900">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            Ready to deploy a screening program that delivers results?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Let's discuss how Kinra can help you close care gaps and improve quality performance.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="/contact"
              className="bg-[#093a3e] text-white px-10 py-5 rounded-full text-lg font-semibold hover:bg-[#062b2f] hover:shadow-2xl hover:scale-105 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2 group"
            >
              <span>Request a Demo</span>
              <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform"></i>
            </a>
            <a
              href="/contact"
              className="bg-white text-[#093a3e] px-10 py-5 rounded-full text-lg font-semibold hover:bg-gray-100 hover:shadow-2xl hover:scale-105 transition-all cursor-pointer whitespace-nowrap inline-flex items-center gap-2"
            >
              <span>Talk to an Expert</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
